import React, { useEffect } from 'react';
import 'twin.macro';
import { useElevesData } from 'queries/eleves/useEleves';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useLocation } from 'react-router-dom';
import { elevesUrls } from 'containers/Eleves/urls';
import { AvatarCard } from 'components/AvatarCard';
import { format } from 'utils/time/format';
import { useTranslation } from 'react-i18next';
import SchoolyearButtonLink from 'components/Buttons/SchoolyearButtonLink';
import { ArrowLeftIcon as ArrowLeft } from '@heroicons/react/24/outline';
import { SortButton } from 'components/SortElevesButton';
import useUpdateUser from 'queries/user/useUpdateUser';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { getSortFieldsOrder } from 'containers/CarnetsDeNotes/CarnetDetails/Body/getSortFieldsOrder';
import Link from 'components/Link';
import { SubSidebar } from 'components/SubSidebar';
import { sortEleves } from 'containers/CarnetsDeNotes/CarnetDetails/sortEleves';
import { UserLocale } from '@teetsh/app/src/types/user';

export function Sidebar() {
  const { pathname } = useLocation();
  const { t } = useTranslation(['eleve', 'common']);
  const { data, isLoading } = useElevesData();
  const eleves = data || [];
  const location = useLocation();
  const viewId = new URLSearchParams(location.search).get('viewId');
  const userPref = useUser();
  const { elevesSort } = userPref;
  const updateUser = useUpdateUser();
  useEffect(() => {
    // Find the active element based on the pathname
    const activeEleve = (eleves || []).find(eleve =>
      pathname.includes(eleve.id),
    );
    if (activeEleve) {
      // Find the DOM node for the active element
      const activeElement = document.querySelector(`#eleve-${activeEleve.id}`);
      if (activeElement) {
        // Focus on the active element
        (activeElement as any)?.focus();
      }
    }
  }, [pathname]);
  return (
    <SubSidebar order={0} collapsedSize={3} minSize={15}>
      <div tw="pl-2 pr-6 py-2 border-b flex justify-between">
        <SchoolyearButtonLink
          minimal
          icon={ArrowLeft}
          to={viewId ? elevesUrls.viewDetails(viewId) : elevesUrls.views()}
        >
          {t('common:buttons.back')}
        </SchoolyearButtonLink>
        <SortButton
          portal
          fieldName={elevesSort?.fieldName || 'familyName'}
          onChange={({ fieldName, direction }) =>
            updateUser({ ...userPref, elevesSort: { fieldName, direction } })
          }
          fieldLabel={t(
            `eleve:fields.${elevesSort?.fieldName || 'familyName'}`,
            {
              defaultValue: '',
            },
          )}
          direction={elevesSort?.direction || 'ASC'}
        />
      </div>
      <nav
        css={[
          tw`flex-1 min-h-0 overflow-y-auto`,
          css`
            max-height: calc(100vh - 120px);
            min-height: calc(100vh - 120px);
          `,
        ]}
        aria-label="Eleves sidebar"
      >
        <ul tw="relative z-0 divide-y divide-gray-200">
          {isLoading && (
            <div tw="space-y-3 p-2">
              {[1, 2, 3].map(v => (
                <li key={v} className="tsh-skeleton" tw="h-20 rounded-lg" />
              ))}
            </div>
          )}
          {sortEleves(
            eleves,
            getSortFieldsOrder(userPref?.elevesSort?.fieldName),
            userPref?.elevesSort?.direction,
          ).map(eleve => (
            <li
              id={`eleve-${eleve.id}`}
              key={eleve.id}
              tabIndex={-1} // Make the list item focusable
            >
              <div
                css={[
                  tw`relative px-5 py-3 flex items-center space-x-3 hover:bg-neutral-quiet`,
                  pathname.includes(eleve.id) && tw`bg-neutral-quiet`,
                ]}
              >
                <div tw="flex-1 min-w-0">
                  <Link
                    to={pathname.replace(
                      /\/eleves\/[^/]+/,
                      `/eleves/${eleve.id}`,
                    )}
                    tw="focus:outline-none"
                  >
                    <div tw="relative flex items-center space-x-3 px-4 py-3 focus-within:ring-2 focus-within:ring-inset focus-within:ring-pink-500">
                      <div tw="flex-shrink-0">
                        <AvatarCard
                          picture={
                            eleve.picture === 'user' ? undefined : eleve.picture
                          }
                        />
                      </div>
                      <div tw="min-w-0 flex-1">
                        <span tw="absolute inset-0" aria-hidden="true"></span>
                        <p tw="text-sm font-medium text-neutral-intense">
                          {eleve.givenName} {eleve.familyName}
                        </p>
                        {eleve.birthday && (
                          <p tw="truncate text-sm text-neutral-quiet">
                            {t('eleve:born', {
                              date: format(
                                eleve.birthday,
                                t('common:locale.long') as UserLocale,
                                t('common:datePattern.dd/MM/yyyy'),
                              ),
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </nav>
    </SubSidebar>
  );
}
